import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/hooks/use-toast";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { authAPI } from "@/services/api";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, User, Mail, Phone, Building, Lock, Sparkles, CheckCircle2 } from "lucide-react";
import SuccessModal from "@/components/modals/SuccessModal";

interface RegisterForm {
  firstName: string;
  lastName: string;
  company: string;
  phone: string;
  email: string;
  password: string;
  confirmPassword: string;
  acceptTerms: boolean;
}

interface FormErrors {
  [key: string]: string;
}

export default function Register() {
  const [form, setForm] = useState<RegisterForm>({
    firstName: '',
    lastName: '',
    company: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const navigate = useNavigate();

  // Validação em tempo real da senha
  const validatePassword = (password: string) => {
    let strength = 0;
    const checks = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };

    Object.values(checks).forEach(check => {
      if (check) strength++;
    });

    setPasswordStrength(strength);
    return checks;
  };

  // Validação de campos
  const validateField = (name: string, value: string): string => {
    switch (name) {
      case 'firstName':
        if (!value.trim()) return 'Nome é obrigatório';
        if (value.trim().length < 2) return 'Nome deve ter pelo menos 2 caracteres';
        if (!/^[a-zA-ZÀ-ÿ\s]+$/.test(value)) return 'Nome deve conter apenas letras';
        return '';

      case 'lastName':
        if (!value.trim()) return 'Sobrenome é obrigatório';
        if (value.trim().length < 2) return 'Sobrenome deve ter pelo menos 2 caracteres';
        if (!/^[a-zA-ZÀ-ÿ\s]+$/.test(value)) return 'Sobrenome deve conter apenas letras';
        return '';

      case 'email':
        if (!value.trim()) return 'Email é obrigatório';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return 'Email inválido';
        return '';

      case 'phone':
        if (value && !/^[\d\s\(\)\-\+]{10,}$/.test(value.replace(/\s/g, ''))) {
          return 'Formato de telefone inválido';
        }
        return '';

      case 'password':
        if (!value) return 'Senha é obrigatória';
        const checks = validatePassword(value);
        if (!checks.length) return 'Senha deve ter pelo menos 8 caracteres';
        if (!checks.lowercase) return 'Senha deve conter pelo menos uma letra minúscula';
        if (!checks.uppercase) return 'Senha deve conter pelo menos uma letra maiúscula';
        if (!checks.number) return 'Senha deve conter pelo menos um número';
        return '';

      case 'confirmPassword':
        if (!value) return 'Confirmação de senha é obrigatória';
        if (value !== form.password) return 'Senhas não coincidem';
        return '';

      default:
        return '';
    }
  };

  // Handle input change
  const handleInputChange = (name: string, value: string) => {
    setForm(prev => ({ ...prev, [name]: value }));

    // Validação em tempo real
    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));

    // Revalidar confirmação de senha se a senha mudou
    if (name === 'password' && form.confirmPassword) {
      const confirmError = validateField('confirmPassword', form.confirmPassword);
      setErrors(prev => ({ ...prev, confirmPassword: confirmError }));
    }
  };

  // Validar formulário completo
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    Object.keys(form).forEach(key => {
      if (key !== 'acceptTerms' && key !== 'company' && key !== 'phone') {
        const error = validateField(key, form[key as keyof RegisterForm] as string);
        if (error) newErrors[key] = error;
      }
    });

    if (!form.acceptTerms) {
      newErrors.acceptTerms = 'Você deve aceitar os termos de serviço';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  // 
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast({
        title: "Erro de validação",
        description: "Por favor, corrija os campos marcados em vermelho.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      await authAPI.register(form);
      setShowSuccessModal(true);

    } catch (error: unknown) {
      toast({
        title: "Erro ao criar conta",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Adicione esta função para lidar com o modal:
  const handleModalClose = () => {
    setShowSuccessModal(false);
    navigate("/login");
  };

  // Adicione esta função aqui:
  const handleResendEmail = async () => {
    try {
      await authAPI.resendVerificationEmail(form.email);
      toast({
        title: "Email reenviado!",
        description: "Verifique sua caixa de entrada.",
      });
    } catch (error: unknown) {
      toast({
        title: "Erro ao reenviar",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  // Strength indicator
  const getPasswordStrengthColor = () => {
    if (passwordStrength < 2) return 'bg-red-500';
    if (passwordStrength < 4) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength < 2) return 'Fraca';
    if (passwordStrength < 4) return 'Média';
    return 'Forte';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/15 via-accent/10 to-primary/20">
      <Helmet>
        <title>Criar Conta — AlphaLabs | Portal do Cliente</title>
        <meta name="description" content="Crie sua conta AlphaLabs e acesse nossos serviços de desenvolvimento e cibersegurança com portal exclusivo do cliente." />
        <link rel="canonical" href="/register" />
      </Helmet>

      {/* Background Effects */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary/10 rounded-full filter blur-3xl opacity-30"></div>
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-accent/10 rounded-full filter blur-3xl opacity-30"></div>
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-lg">
          <div className="bg-card/80 backdrop-blur-sm border border-border/50 shadow-2xl rounded-2xl p-8">

            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 rounded-full text-sm font-medium text-primary mb-4">
                <Sparkles className="w-4 h-4" />
                <span>Portal do Cliente</span>
              </div>

              <h1 className="text-3xl md:text-4xl font-bold mb-2">
                <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  Criar Conta
                </span>
              </h1>
              <p className="text-muted-foreground">
                Junte-se à AlphaLabs e acesse seu portal exclusivo
              </p>
            </div>

            <form onSubmit={onSubmit} className="space-y-6">
              {/* Nome e Sobrenome */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName" className="text-sm font-medium flex items-center gap-2">
                    <User className="w-4 h-4 text-primary" />
                    Nome *
                  </Label>
                  <Input
                    id="firstName"
                    type="text"
                    value={form.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className={`h-11 ${errors.firstName ? 'border-red-500 focus:border-red-500' : 'border-border focus:border-primary'}`}
                    placeholder="Digite seu nome"
                  />
                  {errors.firstName && (
                    <p className="text-xs text-red-500 mt-1">{errors.firstName}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lastName" className="text-sm font-medium">
                    Sobrenome *
                  </Label>
                  <Input
                    id="lastName"
                    type="text"
                    value={form.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className={`h-11 ${errors.lastName ? 'border-red-500 focus:border-red-500' : 'border-border focus:border-primary'}`}
                    placeholder="Digite seu sobrenome"
                  />
                  {errors.lastName && (
                    <p className="text-xs text-red-500 mt-1">{errors.lastName}</p>
                  )}
                </div>
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium flex items-center gap-2">
                  <Mail className="w-4 h-4 text-primary" />
                  Email *
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`h-11 ${errors.email ? 'border-red-500 focus:border-red-500' : 'border-border focus:border-primary'}`}
                  placeholder="seu@email.com"
                />
                {errors.email && (
                  <p className="text-xs text-red-500 mt-1">{errors.email}</p>
                )}
              </div>

              {/* Empresa e Telefone */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="company" className="text-sm font-medium flex items-center gap-2">
                    <Building className="w-4 h-4 text-primary" />
                    Empresa
                  </Label>
                  <Input
                    id="company"
                    type="text"
                    value={form.company}
                    onChange={(e) => handleInputChange('company', e.target.value)}
                    className="h-11 border-border focus:border-primary"
                    placeholder="Nome da empresa"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-sm font-medium flex items-center gap-2">
                    <Phone className="w-4 h-4 text-primary" />
                    Telefone
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className={`h-11 ${errors.phone ? 'border-red-500 focus:border-red-500' : 'border-border focus:border-primary'}`}
                    placeholder="(11) 99999-9999"
                  />
                  {errors.phone && (
                    <p className="text-xs text-red-500 mt-1">{errors.phone}</p>
                  )}
                </div>
              </div>

              {/* Senha */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium flex items-center gap-2">
                  <Lock className="w-4 h-4 text-primary" />
                  Senha *
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className={`h-11 pr-12 ${errors.password ? 'border-red-500 focus:border-red-500' : 'border-border focus:border-primary'}`}
                    placeholder="Digite uma senha forte"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>

                {/* Password Strength */}
                {form.password && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all duration-300 ${getPasswordStrengthColor()}`}
                          style={{ width: `${(passwordStrength / 5) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium">{getPasswordStrengthText()}</span>
                    </div>
                  </div>
                )}

                {errors.password && (
                  <p className="text-xs text-red-500 mt-1">{errors.password}</p>
                )}
              </div>

              {/* Confirmar Senha */}
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-medium">
                  Confirmar Senha *
                </Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={form.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    className={`h-11 pr-12 ${errors.confirmPassword ? 'border-red-500 focus:border-red-500' : 'border-border focus:border-primary'}`}
                    placeholder="Confirme sua senha"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-xs text-red-500 mt-1">{errors.confirmPassword}</p>
                )}
              </div>

              {/* Termos */}
              <div className="space-y-2">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="acceptTerms"
                    checked={form.acceptTerms}
                    onCheckedChange={(checked) =>
                      setForm(prev => ({ ...prev, acceptTerms: checked as boolean }))
                    }
                    className="mt-1"
                  />
                  <Label htmlFor="acceptTerms" className="text-sm leading-relaxed text-muted-foreground">
                    Eu aceito os{" "}
                    <Link to="https://drive.google.com/file/d/1cNQHFEjoE9cSHoePseuca0ralOGovBFA/view?usp=drive_link" className="text-primary hover:underline font-medium">
                      Termos de Serviço
                    </Link>{" "}
                    e a{" "}
                    <Link to="https://drive.google.com/file/d/1ZJxe9l40eUq0MP_DXlyLF2ocAYp24fJ8/view" className="text-primary hover:underline font-medium">
                      Política de Privacidade
                    </Link>
                  </Label>
                </div>
                {errors.acceptTerms && (
                  <p className="text-xs text-red-500 mt-1">{errors.acceptTerms}</p>
                )}
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={loading || Object.keys(errors).some(key => errors[key])}
                className="w-full h-12 text-base font-medium bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Criando conta...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5" />
                    Criar Conta
                  </div>
                )}
              </Button>
            </form>

            {/* Footer */}
            <div className="text-center mt-8 pt-6 border-t border-border/50">
              <p className="text-sm text-muted-foreground">
                Já tem uma conta?{" "}
                <Link
                  to="/login"
                  className="text-primary hover:text-primary/80 font-medium transition-colors"
                >
                  Fazer login
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={handleModalClose}
        title="Conta criada com sucesso!"
        message="Verificamos que você é um humano de verdade! 😄 Acabamos de enviar um link de verificação para o seu email. Clique nele para ativar sua conta e começar sua jornada conosco."
        buttonText="Entendi, vou verificar meu email"
        buttonAction={handleModalClose}
        showEmailIcon={true}
        showResendButton={true}
        resendEmail={handleResendEmail}
      />
    </div>
  );
}